import type { OfflineEntityMap, OfflineQueueStatus } from "../queue/types";

export interface OfflineSyncInfo {
  status: OfflineQueueStatus;
  lastSyncAt?: number;
  lastError?: string;
}

export interface DecoratedEntity<T> {
  entity: T;
  offline?: OfflineSyncInfo;
}

export const decorateEntity = <T extends { id: string }>(
  entity: T,
  metadata: OfflineEntityMap,
  entityType: "item" | "order"
): T & { offline?: OfflineSyncInfo } => {
  const key = `${entityType}:${entity.id}`;
  const offline = metadata[key];
  if (!offline) {
    return entity;
  }
  return Object.assign({}, entity, { offline }) as T & {
    offline?: OfflineSyncInfo;
  };
};

export const decorateEntities = <T extends { id: string }>(
  items: T[],
  metadata: OfflineEntityMap,
  entityType: "item" | "order"
): Array<T & { offline?: OfflineSyncInfo }> => {
  return items.map((item) => decorateEntity(item, metadata, entityType));
};

export const aggregateOrderOfflineStatus = (
  itemStatuses: OfflineSyncInfo[]
): OfflineSyncInfo | undefined => {
  if (!itemStatuses.length) return undefined;
  if (itemStatuses.some((status) => status.status === "error")) {
    return {
      status: "error",
      lastError:
        itemStatuses.find((status) => status.status === "error")?.lastError,
    };
  }
  if (itemStatuses.some((status) => status.status === "pending")) {
    return { status: "pending" };
  }
  if (itemStatuses.some((status) => status.status === "syncing")) {
    return { status: "syncing" };
  }
  return {
    status: "synced",
    lastSyncAt: Math.max(
      ...itemStatuses
        .map((status) => status.lastSyncAt ?? 0)
        .filter((value) => value > 0)
    ),
  };
};

